import { useEffect, useState, useCallback } from 'react';
import { adminGet, adminPost } from '../adminApi';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Card, Field, GREEN, mono } from './bfsiShared';

export default function BfsiOpsHealthTab({ tenantId }) {
  const [health, setHealth] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ ticket_ref: '', scope: 'LAST_24H_LOCATION', duration_minutes: 120, reason: '', device_ids_text: '' });
  const [devices, setDevices] = useState([]);

  const load = useCallback(() => {
    adminGet(`/customers/${tenantId}/ops-health`).then(setHealth).catch(e => toast.error(e.message));
    adminGet('/support-access-requests').then(all => setReqs(all.filter(r => r.tenant_id === tenantId))).catch(() => {});
    adminGet(`/customers/${tenantId}/provisioning`).then(d => setDevices(d.devices)).catch(() => {});
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const wanted = form.device_ids_text.split(',').map(s => s.trim()).filter(Boolean);
      const ids = devices.filter(d => wanted.includes(d.track0tic_id)).map(d => d.device_id);
      await adminPost('/support-access-requests', {
        tenant_id: tenantId, ticket_ref: form.ticket_ref, scope: form.scope,
        duration_minutes: Number(form.duration_minutes), reason: form.reason, device_ids: ids,
      });
      toast.success('Sent to customer for approval');
      setForm({ ticket_ref: '', scope: 'LAST_24H_LOCATION', duration_minutes: 120, reason: '', device_ids_text: '' });
      load();
    } catch (e) { toast.error(e.message); }
  };

  if (!health) return <div className="text-sm" style={{ color: '#9a9e96' }}>Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="bfsi-opshealth-tab">
      <div className="space-y-6">
        <Card data-testid="ops-health-card">
          <h3 className="font-bold mb-1" style={{ color: '#1B1D1C' }}>Operations health</h3>
          <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>{health.note}</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="rounded-lg border p-4" style={{ borderColor: '#E5E3DC' }}>
              <div className="text-2xl font-bold" style={{ color: '#1B1D1C' }}>{health.devices_total}</div>
              <div className="text-xs" style={{ color: '#9a9e96' }}>Lockboxes</div>
            </div>
            <div className="rounded-lg border p-4" style={{ borderColor: '#E5E3DC' }}>
              <div className="text-2xl font-bold" style={{ color: GREEN }}>{health.devices_active}</div>
              <div className="text-xs" style={{ color: '#9a9e96' }}>Active</div>
            </div>
            <div className="rounded-lg border p-4" style={{ borderColor: '#E5E3DC' }}>
              <div className="text-lg font-bold" style={mono} data-testid="ops-locations-state">
                {health.locations === 'visible' ? 'Visible' : '•••••• encrypted'}
              </div>
              <div className="text-xs" style={{ color: '#9a9e96' }}>Locations</div>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="font-bold mb-1" style={{ color: '#1B1D1C' }}>Request support access</h3>
          <p className="text-xs mb-4" style={{ color: '#9a9e96' }}>
            Needed to see location or route data for a support case. The customer's admin must approve (e-signature).
            Every request, approval and view is written to the customer's audit log.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Support ticket" value={form.ticket_ref} onChange={v => setForm({ ...form, ticket_ref: v })} testId="sar-ticket-input" placeholder="TKT-42" />
            <div>
              <label className="text-sm block mb-1" style={{ color: '#4b504a' }}>Access needed</label>
              <select data-testid="sar-scope-select" value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })}
                className="w-full h-9 rounded-md border px-3 text-sm bg-white" style={{ borderColor: '#D9D6CD' }}>
                <option value="LAST_24H_LOCATION">Last 24 h location</option>
                <option value="LIVE_LOCATION">Live location</option>
                <option value="TRIP_ROUTE">Trip route</option>
              </select>
            </div>
            <Field label="Devices (Track0tic IDs, comma separated)" value={form.device_ids_text} onChange={v => setForm({ ...form, device_ids_text: v })} testId="sar-devices-input" placeholder="TRK-LBX-0001" />
            <Field label="Duration (minutes, max 1440)" value={form.duration_minutes} onChange={v => setForm({ ...form, duration_minutes: v })} testId="sar-duration-input" type="number" />
          </div>
          <textarea data-testid="sar-reason-input" value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
            rows={2} placeholder="Reason" className="w-full mt-3 rounded-md border px-3 py-2 text-sm" style={{ borderColor: '#D9D6CD' }} />
          <Button data-testid="sar-submit-btn" onClick={submit} disabled={!form.ticket_ref.trim() || !form.reason.trim()}
            className="mt-3 text-white" style={{ background: GREEN }}>Send to customer for approval</Button>
        </Card>
      </div>

      <Card data-testid="sar-list-card">
        <h3 className="font-bold mb-3" style={{ color: '#1B1D1C' }}>Support access requests</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase" style={{ color: '#9a9e96' }}>
              <th className="py-2">Ticket</th><th className="py-2">Scope</th><th className="py-2">By</th><th className="py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {reqs.length === 0 && <tr><td colSpan="4" className="py-4 text-center" style={{ color: '#9a9e96' }}>No requests</td></tr>}
            {reqs.map(r => (
              <tr key={r.id} className="border-t" style={{ borderColor: '#F0EEE8' }}>
                <td className="py-2" style={mono}>{r.ticket_ref}</td>
                <td className="py-2 text-xs">{r.scope} · {r.duration_minutes} min</td>
                <td className="py-2 text-xs">{r.requested_by}</td>
                <td className="py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={r.grant_active ? { background: '#E3F1EC', color: GREEN }
                      : r.status === 'REJECTED' ? { background: '#FBEAE5', color: '#B3401F' }
                        : { background: '#FDF3E0', color: '#9a6b12' }}>
                    {r.grant_active ? `Active until ${new Date(r.grant_ends_at).toLocaleTimeString()}` : r.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
